import React, { useState, useEffect } from 'react';

/**
 * UTF-8 Safe Base64 Decoder
 */
export function decodeContactText(str) {
  if (!str) return '';
  try {
    return decodeURIComponent(
      atob(str)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
  } catch (e) {
    try {
      return atob(str);
    } catch (err) {
      return str;
    }
  }
}

/**
 * String Reverser for CSS BIDI Obfuscation
 */
export function reverseString(str) {
  if (!str) return '';
  return Array.from(str).reverse().join('');
}

/**
 * Common pre-encoded contact constants for American Mazu Foundation
 */
export const ENCODED_CONTACTS = {
  PHONE: 'OTE3LTYyMi0zMzMz',
  FAX: 'MjEyLTk2Mi02NjMz',
  EMAIL: 'Y29udGFjdEB1c21henUub3Jn',
  ADDRESS_FULL: 'MTMyLTE1IDQxc3QgQXZlLiAxQiwgRmx1c2hpbmcsIE5ZIDExMzU0',
  ADDRESS_SHORT: 'MTMyLTE1IDQxc3QgQXZlLiAxQiwgRmx1c2hpbmcgTlkgMTEzNTQ=',
  CEREMONY_LOCATION: '5aSn57SNwrXms5XnrokgMTMyLTE1IDQxIEF2Ze%2B9iOmmponmoLzph4zlpKflp4Yo',
};

/**
 * ProtectedContact Component
 * - Base64 encoded payload in source code.
 * - Dynamic client-side decoding on mount.
 * - Reversed text in DOM nodes with CSS `direction: rtl` and `unicode-bidi: bidi-override`.
 *   Human eyes see normal text; DOM inspectors, scrapers, and bots read reversed text (e.g. 3366-269-212).
 * - Google's `data-nosnippet` attribute attached.
 * - No raw tel: or mailto: URLs exposed in DOM href attributes.
 */
export default function ProtectedContact({
  encoded,
  type = 'text', // 'text' | 'tel' | 'mailto'
  className = '',
  prefix = '',
  suffix = '',
  children,
  ...props
}) {
  const [value, setValue] = useState('');

  useEffect(() => {
    setValue(decodeContactText(encoded));
  }, [encoded]);

  // While mounting/hydrating, render a subtle placeholder container with data-nosnippet
  if (!value) {
    return <span data-nosnippet className={className}>...</span>;
  }

  const reversedValue = reverseString(value);
  const reversedPrefix = prefix ? reverseString(prefix) : '';
  const reversedSuffix = suffix ? reverseString(suffix) : '';

  // CSS BIDI Override style:
  // Reverses visual character rendering back to normal for humans
  const bidiStyle = {
    direction: 'rtl',
    unicodeBidi: 'bidi-override',
    display: 'inline-block',
  };

  const handleAction = (e) => {
    if (type === 'tel') {
      e.preventDefault();
      const rawNumber = value.replace(/[^0-9+]/g, '');
      window.location.href = `tel:${rawNumber}`;
    } else if (type === 'mailto') {
      e.preventDefault();
      window.location.href = `mailto:${value}`;
    }
  };

  if (type === 'tel' || type === 'mailto') {
    return (
      <a
        href="#"
        onClick={handleAction}
        className={`cursor-pointer ${className}`}
        data-nosnippet
        {...props}
      >
        {children}
        <span style={bidiStyle}>
          {reversedSuffix}{reversedValue}{reversedPrefix}
        </span>
      </a>
    );
  }

  return (
    <span className={className} data-nosnippet {...props}>
      {children}
      <span style={bidiStyle}>
        {reversedSuffix}{reversedValue}{reversedPrefix}
      </span>
    </span>
  );
}
